import TooltipEllipsis from '@deltek/specification-client/core/Feedbacks/TooltipEllipsis'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import { CdnBaseUrl } from '@deltek/specification-client/core/Utilities/urlUtils'
import { Typography } from '@material-ui/core'
import IconButton from '@material-ui/core/IconButton'
import { makeStyles, Theme } from '@material-ui/core/styles'
import SvgIcon from '@material-ui/core/SvgIcon'
import MoreVertOutlinedIcon from '@material-ui/icons/MoreVertOutlined'
import clsx from 'clsx'
import React from 'react'
import { getProfessionalDisciplineViewNames } from 'shared/utils/carouselCard'

const useStyles = makeStyles((theme: Theme) => ({
    projectGridItemContainer: {
        minHeight: pxToRem(115),
        position: 'relative',
        maxWidth: pxToRem(200),
        width: '95%',
        marginRight: theme.spacing(1.25),
    },
    anchorImg: {
        height: pxToRem(85),
    },
    cardBackground: {
        background: '#F5F8FA',
    },
    whiteBackground: {
        background: theme.palette.common.white,
    },
    projectGridItemBar: {
        background: theme.palette.secondary.main,
        height: 30,
        width: '100%',
        bottom: 0,
        position: 'relative',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: theme.spacing(1),
        paddingRight: theme.spacing(0.5),
    },
    projectGridItemName: {
        ...theme.typography.caption,
        textDecoration: 'none',
        zIndex: 1,
    },
    whiteFont: {
        color: theme.palette.common.white,
    },
    favoriteIcon: {
        color: theme.palette.common.white,
    },
    gridItemIcons: {
        color: '#DADADA',
    },
    noFillIcon: {
        fill: 'none',
    },
    slimIcon: {
        width: 15,
    },
    projectImage: {
        height: pxToRem(85),
        position: 'relative',
    },
    projectTeamDisciplineName: {
        fontSize: pxToRem(48),
        paddingTop: theme.spacing(3.5),
        paddingLeft: theme.spacing(0.5),
        height: '100%',
        overflow: 'hidden',
    },
    projectTeamDisciplineIcon: {
        paddingTop: theme.spacing(1),
        paddingLeft: theme.spacing(0.5),
        width: pxToRem(80),
        height: pxToRem(80),
    },
    projectIcon: {
        position: 'absolute',
        right: 0,
    },
    projectTeamDisciplineItem: {
        position: 'absolute',
        left: 2,
        right: 2,
        zIndex: 1,
        height: '100%',
    },
    iconContainer: {
        cursor: 'pointer',
        position: 'absolute',
        right: 0,
    },
}))

const CdnDisciplineBaseUrl = `${CdnBaseUrl}/specpoint/ae/disciplines`

interface CarouselCardProps {
    handlePageIconClick?: (
        event: React.MouseEvent,
        projectTeamName: string
    ) => void
    handleEllipsisIconClick?: (event: React.MouseEvent) => void
    projectTeamName?: string
    setProjectTeam?: (projectTeamName: null | string) => void
    variant?: 'readOnly' | 'full'
}
const CarouselCard: React.FC<CarouselCardProps> = ({
    handlePageIconClick,
    handleEllipsisIconClick,
    projectTeamName,
    setProjectTeam,
    variant,
}) => {
    const classes = useStyles()
    const abbreviatedTeamDescObject = projectTeamName
        ? getProfessionalDisciplineViewNames(projectTeamName)
        : { abbName: '', fullName: '', width: 61, height: 61 }

    const handleTeamMembersListPopup = (event: React.MouseEvent) => {
        handlePageIconClick?.(event, projectTeamName!)
    }

    const handleActionEllipsisIconClick = (event: React.MouseEvent) => {
        setProjectTeam?.(projectTeamName ?? null)
        handleEllipsisIconClick?.(event)
    }
    const tooltipWidth =
        (document.getElementById('gridItemContRef')?.offsetWidth ?? 0) * 0.6 ??
        100
    return (
        <div
            id="gridItemContRef"
            className={clsx(
                classes.projectGridItemContainer,
                classes.cardBackground,
                'projectGridItem'
            )}
        >
            <GridContainer className={classes.projectImage}>
                <GridItem className={classes.projectTeamDisciplineItem}>
                    <Typography className={classes.projectTeamDisciplineName}>
                        {abbreviatedTeamDescObject.abbName}
                    </Typography>
                </GridItem>
                <GridItem className={classes.projectIcon}>
                    <img
                        src={
                            abbreviatedTeamDescObject.abbName
                                ? `${CdnDisciplineBaseUrl}/${abbreviatedTeamDescObject.abbName.toLowerCase()}_team.svg`
                                : ''
                        }
                        alt={`${abbreviatedTeamDescObject.abbName} icon`}
                        className={classes.projectTeamDisciplineIcon}
                    />
                </GridItem>
            </GridContainer>
            <div className={classes.projectGridItemBar}>
                <div
                    className={clsx(
                        classes.projectGridItemName,
                        classes.whiteFont
                    )}
                >
                    <TooltipEllipsis
                        text={abbreviatedTeamDescObject.fullName}
                        width={tooltipWidth}
                    />
                </div>
                <div className={classes.iconContainer}>
                    <IconButton
                        data-testid="projectTeamPageIcon"
                        size="small"
                        onClick={handleTeamMembersListPopup}
                        aria-label={`View ${projectTeamName} Members`}
                    >
                        <SvgIcon
                            viewBox="0 0 16 16"
                            fontSize="small"
                            className={clsx(
                                'projectTeamInfoPopoutAnchor',
                                classes.gridItemIcons,
                                classes.noFillIcon
                            )}
                        >
                            <path
                                d="M11.3333 2H4.66659C3.93021 2 3.33325 2.59695 3.33325 3.33333V12.6667C3.33325 13.403 3.93021 14 4.66659 14H11.3333C12.0696 14 12.6666 13.403 12.6666 12.6667V3.33333C12.6666 2.59695 12.0696 2 11.3333 2Z"
                                stroke="currentcolor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M6 4.66667H10"
                                stroke="currentcolor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M6 7.33333H10"
                                stroke="currentcolor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                            <path
                                d="M6 10H8.66667"
                                stroke="currentcolor"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                            />
                        </SvgIcon>
                    </IconButton>
                    {variant === 'full' && (
                        <IconButton
                            data-testid="projectTeamEllipsisIcon"
                            size="small"
                            className={clsx(
                                classes.slimIcon,
                                'projectTeamEllipsis'
                            )}
                            onClick={handleActionEllipsisIconClick}
                            aria-label={`More Options for ${projectTeamName}`}
                        >
                            <MoreVertOutlinedIcon
                                fontSize="small"
                                className={clsx(
                                    'projectTeamMenuAnchor',
                                    classes.gridItemIcons
                                )}
                            />
                        </IconButton>
                    )}
                </div>
            </div>
        </div>
    )
}

export default CarouselCard
